import React, { useEffect, useState } from "react";
import { jiraUrl } from "../../constants/api";
import { TYPE, FONT_FAMILY } from "../../constants/theme";

const MONO = TYPE.mono.fontFamily;
const SANS = FONT_FAMILY;

const STATUS_COLORS = {
  "To Do":      "#9CA3AF",
  "In Progress":"#60A5FA",
  "Testing":    "#C4B5FD",
  "Done":       "#86EFAC",
  "Closed":     "#86EFAC",
  "Resolved":   "#86EFAC",
  "On Hold":    "#FCD34D",
  "Blocked":    "#FCA5A5",
  "Won't Do":   "#9CA3AF",
};
const STATUS_BG = {
  "In Progress": "rgba(96,165,250,0.12)",
  "Testing":     "rgba(196,181,253,0.12)",
  "Done":        "rgba(134,239,172,0.12)",
  "Closed":      "rgba(134,239,172,0.12)",
  "Resolved":    "rgba(134,239,172,0.12)",
  "On Hold":     "rgba(252,211,77,0.12)",
  "Blocked":     "rgba(252,165,165,0.12)",
  "To Do":       "rgba(156,163,175,0.10)",
  "Won't Do":    "rgba(156,163,175,0.10)",
};

function statusColor(s) { return STATUS_COLORS[s] || "rgba(255,255,255,0.5)"; }
function statusBg(s)    { return STATUS_BG[s]     || "rgba(255,255,255,0.07)"; }

function fmtDuration(days) {
  if (days == null) return "—";
  if (days < 1) return `${Math.round(days * 24)}h`;
  if (days < 7) return `${Math.round(days)}d`;
  if (days < 30) return `${Math.round(days / 7)}w`;
  return `${Math.round(days / 30)}mo`;
}

function fmtDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function SkBlock({ w = "100%", h = 12, r = 4, style = {} }) {
  return <div className="idr-sk" style={{ width: w, height: h, borderRadius: r, ...style }} />;
}

function LoadingContent() {
  return (
    <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 28 }}>
      {/* Hero */}
      <div style={{ padding: "16px 18px", borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <SkBlock w={90} h={28} r={6} />
          <SkBlock w={110} h={28} r={6} />
        </div>
        <SkBlock w="60%" h={9} />
      </div>
      {/* Journey */}
      <div>
        <SkBlock w={130} h={9} style={{ marginBottom: 18 }} />
        {[72, 55, 80, 60].map((w, i) => (
          <div key={i} style={{ display: "flex", gap: 14, marginBottom: 16 }}>
            <SkBlock w={8} h={8} r={9} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <SkBlock w={`${w}%`} h={10} style={{ marginBottom: 7 }} />
              <SkBlock w="42%" h={8} />
            </div>
          </div>
        ))}
      </div>
      {/* Ownership */}
      <div>
        <SkBlock w={110} h={9} style={{ marginBottom: 14 }} />
        {[1, 2].map(i => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0" }}>
            <div>
              <SkBlock w={90} h={10} style={{ marginBottom: 7 }} />
              <SkBlock w={130} h={8} />
            </div>
            <SkBlock w={28} h={10} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Section Header ────────────────────────────────────────────────────────────
function SectionHeader({ label, meta, desc }) {
  return (
    <div style={{ marginBottom: 12, paddingBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)" }}>
          {label}
        </span>
        {meta && (
          <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.2)", letterSpacing: "0.04em" }}>
            {meta}
          </span>
        )}
      </div>
      {desc && (
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.28)", lineHeight: 1.5, marginTop: 5 }}>{desc}</div>
      )}
    </div>
  );
}

// ─── Journey Timeline ──────────────────────────────────────────────────────────
function JourneyTimeline({ journey }) {
  if (!journey || journey.length === 0) {
    return (
      <div style={{ padding: "14px 16px", borderRadius: 6, background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.4)", marginBottom: 5 }}>No transition history recorded</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.22)", lineHeight: 1.6 }}>
          This ticket may have been created in its current status, or status transitions were not captured at the time of sync.
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {journey.map((stage, i) => {
        const isCurrent = stage.isCurrent;
        const isBounce  = stage.isBounce;
        const sc        = statusColor(stage.status);
        const dotColor  = isCurrent ? sc : isBounce ? "#FB923C" : "rgba(255,255,255,0.18)";
        const isLast    = i === journey.length - 1;
        return (
          <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
            {/* Axis */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 16, flexShrink: 0, paddingTop: isCurrent ? 10 : 3 }}>
              <div style={{
                width: isCurrent ? 10 : 8, height: isCurrent ? 10 : 8, borderRadius: "50%",
                background: dotColor, flexShrink: 0,
                boxShadow: isCurrent ? `0 0 8px ${sc}70` : "none",
              }} />
              {!isLast && (
                <div style={{
                  width: 1, flex: 1, minHeight: 16,
                  background: isBounce ? "rgba(251,146,60,0.25)" : "rgba(255,255,255,0.07)",
                  margin: "4px 0 0",
                }} />
              )}
            </div>
            {/* Content */}
            <div style={{
              flex: 1,
              paddingBottom: isLast ? 0 : 14,
              ...(isCurrent ? {
                background: statusBg(stage.status),
                border: `1px solid ${sc}25`,
                borderRadius: 6,
                padding: "8px 10px",
                marginBottom: isLast ? 0 : 14,
              } : {}),
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, fontWeight: isCurrent ? 700 : 600, color: isCurrent ? sc : "rgba(255,255,255,0.72)" }}>
                  {stage.status}
                </span>
                {isBounce && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 5px", borderRadius: 3, background: "rgba(251,146,60,0.15)", color: "#FB923C", letterSpacing: "0.05em" }}>↩ BOUNCE</span>
                )}
                {isCurrent && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 5px", borderRadius: 3, background: `${sc}18`, border: `1px solid ${sc}35`, color: sc, letterSpacing: "0.05em" }}>NOW</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.28)", marginTop: 3, display: "flex", gap: 8 }}>
                <span>{fmtDuration(stage.durationDays)}</span>
                {stage.enteredAt && <span>· from {fmtDate(stage.enteredAt)}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Ownership Timeline ────────────────────────────────────────────────────────
function OwnershipTimeline({ timeline }) {
  if (!timeline || timeline.length === 0) {
    return (
      <div style={{ padding: "14px 16px", borderRadius: 6, background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.4)", marginBottom: 5 }}>No ownership records found</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.22)", lineHeight: 1.6 }}>
          This ticket is currently unassigned. Ownership history is tracked from the first assignment.
        </div>
      </div>
    );
  }
  return (
    <div style={{ borderRadius: 6, overflow: "hidden", border: "1px solid rgba(255,255,255,0.08)" }}>
      {timeline.map((entry, i) => (
        <div key={i} style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "11px 14px",
          background: entry.isCurrent ? "rgba(147,197,253,0.06)" : i % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent",
          borderBottom: i < timeline.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
          borderLeft: `3px solid ${entry.isCurrent ? "#93C5FD" : "transparent"}`,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <span style={{
                fontSize: 12, fontWeight: 600,
                color: entry.isCurrent ? "#93C5FD" : "rgba(255,255,255,0.72)",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200,
              }}>
                {entry.owner || "Unassigned"}
              </span>
              {entry.isCurrent && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: "rgba(147,197,253,0.1)", color: "#93C5FD", letterSpacing: "0.05em", flexShrink: 0 }}>NOW</span>
              )}
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.28)" }}>
              {fmtDate(entry.startedAt)} → {entry.endedAt ? fmtDate(entry.endedAt) : "Present"}
            </div>
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: entry.isCurrent ? "rgba(147,197,253,0.55)" : "rgba(255,255,255,0.28)", marginLeft: 12, flexShrink: 0 }}>
            {fmtDuration(entry.durationDays)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── PR Card ──────────────────────────────────────────────────────────────────
function PRCard({ pr }) {
  const [hov, setHov] = useState(false);
  const sc = pr.merged ? "#86EFAC" : pr.state === "open" ? "#93C5FD" : "#9CA3AF";
  const sbg = pr.merged ? "rgba(134,239,172,0.1)" : pr.state === "open" ? "rgba(147,197,253,0.1)" : "rgba(156,163,175,0.08)";
  return (
    <a href={pr.url} target="_blank" rel="noopener noreferrer"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "block", padding: "12px 14px", borderRadius: 6, textDecoration: "none",
        background: hov ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.025)",
        border: `1px solid ${hov ? `${sc}30` : "rgba(255,255,255,0.08)"}`,
        transition: "all 0.15s ease",
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: hov ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.78)", lineHeight: 1.45, flex: 1, fontWeight: 500 }}>
          {pr.prTitle}
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 7px", borderRadius: 3, flexShrink: 0, letterSpacing: "0.06em", background: sbg, color: sc, border: `1px solid ${sc}28` }}>
          {pr.merged ? "MERGED" : pr.state === "open" ? "OPEN" : "CLOSED"}
        </span>
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.25)", fontFamily: MONO }}>#{pr.prNumber}</span>
        <span style={{ width: 3, height: 3, borderRadius: "50%", background: "rgba(255,255,255,0.15)", flexShrink: 0 }} />
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.25)" }}>{pr.repository}</span>
        <span style={{ width: 3, height: 3, borderRadius: "50%", background: "rgba(255,255,255,0.15)", flexShrink: 0 }} />
        <span style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", fontWeight: 500 }}>{pr.author}</span>
      </div>
    </a>
  );
}

function LinkedPRs({ prs }) {
  if (!prs || prs.length === 0) {
    return (
      <div style={{ padding: "14px 16px", borderRadius: 6, background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.4)", marginBottom: 5 }}>No linked pull requests</div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.22)", lineHeight: 1.6 }}>
          PRs are linked when a branch name or description references this ticket key (e.g. DIG-1234). Uncommitted branches won't appear here.
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {prs.map((pr, i) => <PRCard key={i} pr={pr} />)}
    </div>
  );
}

// ─── Close Button ─────────────────────────────────────────────────────────────
function CloseBtn({ onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 4, padding: "6px 10px",
        color: hov ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.4)",
        fontSize: 12, cursor: "pointer", flexShrink: 0,
        transition: "all 0.15s ease",
      }}
      title="Close (Esc)"
    >
      ✕
    </button>
  );
}

// ─── Error / Empty Body ────────────────────────────────────────────────────────
function ErrorBody() {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 28px", textAlign: "center" }}>
      <div style={{ fontSize: 28, marginBottom: 12, opacity: 0.35 }}>◌</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>No lifecycle data available</div>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.22)", lineHeight: 1.7, maxWidth: 280 }}>
        This ticket may not exist in the system yet, or lifecycle data hasn't been synced. Try running a manual sync from the dashboard header.
      </div>
    </div>
  );
}

// ─── Main drawer body when data is loaded ─────────────────────────────────────
function DrawerBody({ data, prs }) {
  const sc           = statusColor(data.currentStatus);
  const bounceCount  = data.bounceCount ?? 0;
  const ownerCount   = data.ownershipTimeline?.length ?? 0;

  return (
    <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 24, fontFamily: SANS }}>

      {/* ── Current State Hero ── */}
      <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.07)", padding: "16px 18px" }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Pill label="Status" value={data.currentStatus || "Unknown"} valueStyle={{ color: sc, background: `${sc}18`, border: `1px solid ${sc}30` }} />
          <Pill label="Assigned To" value={data.currentOwner || "Unassigned"} />
          {data.resolvedDaysAgo != null && (
            <Pill label="Resolved" value={`${Math.round(data.resolvedDaysAgo)}d ago`} valueStyle={{ color: "#86EFAC", background: "rgba(134,239,172,0.1)", border: "1px solid rgba(134,239,172,0.2)" }} />
          )}
        </div>
        {bounceCount > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
            <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 3, background: "rgba(251,146,60,0.14)", border: "1px solid rgba(251,146,60,0.22)", color: "#FB923C", letterSpacing: "0.05em", flexShrink: 0 }}>
              ↩ {bounceCount} BOUNCE{bounceCount !== 1 ? "S" : ""}
            </span>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.28)", lineHeight: 1.45 }}>
              Moved backwards in the workflow {bounceCount} time{bounceCount !== 1 ? "s" : ""}.
            </span>
          </div>
        )}
      </div>

      {/* ── Status Journey ── */}
      <div>
        <SectionHeader
          label="Status Journey"
          desc="Every status this ticket has passed through. A bounce means it moved backward, e.g. Testing → In Progress."
          meta={`${data.totalStages ?? 0} stage${data.totalStages !== 1 ? "s" : ""}${bounceCount > 0 ? ` · ${bounceCount} bounce${bounceCount !== 1 ? "s" : ""}` : ""}`}
        />
        <JourneyTimeline journey={data.journey} />
      </div>

      {/* ── Ownership ── */}
      <div>
        <SectionHeader
          label="Ownership History"
          desc="Everyone who has been assigned this ticket, and how long each one held it."
          meta={ownerCount > 0 ? `${ownerCount} owner${ownerCount !== 1 ? "s" : ""}` : null}
        />
        <OwnershipTimeline timeline={data.ownershipTimeline} />
      </div>

      {/* ── Linked PRs ── */}
      <div>
        <SectionHeader
          label="Linked Pull Requests"
          desc="GitHub pull requests whose branch name or description references this ticket's key."
          meta={prs.length > 0 ? `${prs.length} PR${prs.length !== 1 ? "s" : ""}` : null}
        />
        <LinkedPRs prs={prs} />
      </div>

    </div>
  );
}

function Pill({ label, value, valueStyle = {} }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.28)" }}>
        {label}
      </div>
      <span style={{
        fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 4,
        background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
        color: "rgba(255,255,255,0.8)",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 160,
        ...valueStyle,
      }}>
        {value}
      </span>
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────
export default function InvestigationDrawer({ ticketKey, data, prs = [], loading, onClose }) {
  const [visible, setVisible] = useState(false);

  // Slide-in on every new ticket
  useEffect(() => {
    setVisible(false);
    if (!ticketKey) return;
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, [ticketKey]);

  // Escape key closes drawer
  useEffect(() => {
    const handle = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [onClose]);

  if (!ticketKey) return null;

  const accentColor = data ? statusColor(data.currentStatus) : "rgba(255,255,255,0.1)";

  return (
    <>
      {/* Keyframe injection */}
      <style>{`
        .idr-sk {
          background: rgba(255,255,255,0.08);
          animation: idr-pulse 1.6s ease-in-out infinite;
        }
        @keyframes idr-pulse {
          0%,100% { opacity: 0.45; }
          50%      { opacity: 0.9; }
        }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 999, background: "rgba(0,0,0,0.45)" }}
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-label={`Investigating ${ticketKey}`}
        style={{
          position: "fixed", top: 0, right: 0, bottom: 0, width: 440,
          background: "#061729",
          borderLeft: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "-10px 0 40px rgba(0,0,0,0.55)",
          zIndex: 1000,
          overflowY: "auto",
          display: "flex", flexDirection: "column",
          fontFamily: SANS,
          transform: visible ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.28s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Status accent stripe */}
        <div style={{ height: 3, background: accentColor, flexShrink: 0, transition: "background 0.4s ease" }} />

        {/* Header */}
        <div style={{ padding: "18px 24px 14px", borderBottom: "1px solid rgba(255,255,255,0.08)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(255,255,255,0.28)", marginBottom: 6 }}>
                Ticket Investigation
              </div>
              <div style={{ fontFamily: MONO, fontSize: 20, fontWeight: 700, color: "#93C5FD", letterSpacing: "0.01em" }}>
                {ticketKey}
              </div>
            </div>
            <CloseBtn onClick={onClose} />
          </div>
          <a
            href={jiraUrl(ticketKey)}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 11, fontWeight: 600, color: "#60A5FA", textDecoration: "none" }}
            onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")}
            onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}
          >
            Open in Jira ↗
          </a>
        </div>

        {/* Body */}
        {loading  ? <LoadingContent /> : !data ? <ErrorBody /> : <DrawerBody data={data} prs={prs} />}
      </div>
    </>
  );
}
